import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast, Toaster } from 'sonner';
import {
  ArrowLeft, ArrowRight, CheckCircle2, Copy, Download, FileUp, Globe,
  Loader2, Package, Plug, Shield, Store, XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { onboardingApi, activateStoreLocally, hasActiveSession, onboardingSupabase } from '../lib/onboarding';
import { StripeConnectDialog } from '../components/StripeConnectDialog';
import { WooCommerceAPI, wooRequest, loadConfig } from '../lib/woocommerce';
import type { WooCommerceConfig } from '../lib/woocommerce';
import {
  CSV_MAX_BYTES, CSV_TEMPLATE, PRODUCT_FIELDS, autoMapColumns, findDuplicates,
  mapRows, parseCsv, toWooProduct, validateRow,
  type ImportResult, type MappedRow,
} from '../lib/csvImport';
import logoDark from '../../assets/logo-dark.png';
import logoLight from '../../assets/logo-light.png';

type Path = 'woocommerce_api' | 'woocommerce_plugin' | 'native';
type StepId =
  | 'choose-path'
  | 'a-store' | 'a-credentials' | 'a-review'
  | 'b-install' | 'b-pair'
  | 'c-details' | 'c-catalog' | 'c-payments'
  | 'done';

const PATH_STEPS: Record<Path, StepId[]> = {
  woocommerce_api: ['choose-path', 'a-store', 'a-credentials', 'a-review', 'done'],
  woocommerce_plugin: ['choose-path', 'b-install', 'b-pair', 'done'],
  native: ['choose-path', 'c-details', 'c-catalog', 'c-payments', 'done'],
};

interface CsvState {
  fileName: string;
  rows: string[][];
  headers: string[];
  columnMap: (string | null)[];
  mapped: MappedRow[];
  validations: Map<number, ReturnType<typeof validateRow>>;
  duplicates: Map<number, string>;
  dupStrategy: 'skip' | 'import';
}

const emptyCsv: CsvState | null = null;

export function OnboardingWizard() {
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const [step, setStep] = useState<StepId>('choose-path');
  const [path, setPath] = useState<Path | null>(null);
  const [busy, setBusy] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [resumed, setResumed] = useState(false);

  const [form, setForm] = useState({ storeName: '', wpUrl: '', consumerKey: '', consumerSecret: '', currency: 'EUR', slug: '', country: 'PT' });
  const [connTest, setConnTest] = useState<{ state: 'idle' | 'testing' | 'ok' | 'fail'; message?: string }>({ state: 'idle' });
  const [pairing, setPairing] = useState<{ code: string; expiresAt: number; status: 'pending' | 'paired' | 'expired' } | null>(null);
  const [provisioned, setProvisioned] = useState<any>(null);
  const [catalogMode, setCatalogMode] = useState<'skip' | 'product' | 'csv'>('skip');
  const [firstProduct, setFirstProduct] = useState({ name: '', regular_price: '', stock_quantity: '', sku: '' });
  const [csv, setCsv] = useState<CsvState | null>(emptyCsv);
  const [importState, setImportState] = useState<ImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [nativeCfg, setNativeCfg] = useState<WooCommerceConfig | null>(null);
  const [payProvider, setPayProvider] = useState<string>('stripe');
  const [payUsePlatform, setPayUsePlatform] = useState(false);
  const [payFields, setPayFields] = useState<Record<string, string>>({});
  const [savingPayments, setSavingPayments] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // --- Session + resume ---
  useEffect(() => {
    (async () => {
      if (!(await hasActiveSession())) {
        window.location.href = '/signup';
        return;
      }
      try {
        const saved = await onboardingApi.loadProgress();
        if (saved && saved.step && saved.step !== 'done') {
          setResumed(true);
          setStep(saved.step);
          if (saved.path) setPath(saved.path);
          setForm((f) => ({ ...f, ...(saved.form || {}), consumerSecret: '' }));
          if (saved.catalogMode) setCatalogMode(saved.catalogMode);
        }
      } catch { /* resume is best-effort */ }
      setSessionChecked(true);
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const persist = (nextStep: StepId, extra: Record<string, any> = {}) => {
    onboardingApi.saveProgress(nextStep, {
      path, form: { ...form, consumerSecret: form.consumerSecret ? '__set__' : undefined },
      catalogMode, ...extra,
    }).catch(() => {});
  };

  const stepIndex = path ? PATH_STEPS[path].indexOf(step) : 0;
  const go = (next: StepId) => {
    setStep(next);
    persist(next);
    window.scrollTo({ top: 0 });
  };
  const goBack = () => {
    if (!path) return;
    const idx = PATH_STEPS[path].indexOf(step);
    if (idx > 0) go(PATH_STEPS[path][idx - 1]);
  };

  const normalizedWpUrl = () => {
    try {
      const u = new URL(form.wpUrl.startsWith('http') ? form.wpUrl : `https://${form.wpUrl}`);
      return u.origin;
    } catch {
      return null;
    }
  };

  // --- Path A: WooCommerce API ---
  const testConnection = async () => {
    const url = normalizedWpUrl();
    if (!url) { toast.error('URL inválido.'); return; }
    setConnTest({ state: 'testing' });
    try {
      await WooCommerceAPI.testConnection({ url, consumerKey: form.consumerKey.trim(), consumerSecret: form.consumerSecret.trim(), version: 'v3', useProxy: true });
      setConnTest({ state: 'ok' });
      toast.success('Ligação verificada com sucesso!');
    } catch (e: any) {
      setConnTest({ state: 'fail', message: e?.message || 'Falha na ligação' });
    }
  };

  const provisionApi = async () => {
    setBusy(true);
    try {
      const url = normalizedWpUrl();
      const store = await onboardingApi.provisionStore({
        name: form.storeName, connectionType: 'woocommerce_api',
        wpUrl: url!, consumerKey: form.consumerKey.trim(), consumerSecret: form.consumerSecret.trim(),
        onboardingCompleted: true,
      });
      await activateStoreLocally(store, { url: url!, consumerKey: form.consumerKey.trim(), consumerSecret: form.consumerSecret.trim() });
      setProvisioned(store);
      go('done');
    } catch (e: any) {
      toast.error(e?.message || 'Não foi possível criar a loja.');
    } finally {
      setBusy(false);
    }
  };

  // --- Path B: Plugin pairing ---
  const generateCode = async () => {
    setBusy(true);
    try {
      const data = await onboardingApi.createPairingCode(form.storeName || undefined);
      setPairing({ code: data.code, expiresAt: data.expiresAt, status: 'pending' });
    } catch (e: any) {
      toast.error(e?.message || 'Falha ao gerar código.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (step !== 'b-pair' || !pairing || pairing.status !== 'pending') return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await onboardingApi.getPairingStatus(pairing.code);
        if (res.status === 'paired' && res.store && res.config) {
          setPairing((p) => p ? { ...p, status: 'paired' } : p);
          if (pollRef.current) clearInterval(pollRef.current);
          await activateStoreLocally(res.store, {
            url: res.config.wp_url, consumerKey: res.config.consumer_key, consumerSecret: res.config.consumer_secret,
          });
          setProvisioned(res.store);
          toast.success('Plugin emparelhado com sucesso!');
          go('done');
        } else if (res.status === 'expired') {
          setPairing((p) => p ? { ...p, status: 'expired' } : p);
          if (pollRef.current) clearInterval(pollRef.current);
          toast.error('O código expirou. Gere um novo.');
        }
      } catch { /* keep polling — transient errors are normal */ }
    }, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [step, pairing?.code, pairing?.status]);

  // --- CSV ---
  const handleCsvFile = async (file: File) => {
    if (file.size > CSV_MAX_BYTES) {
      toast.error(`Ficheiro demasiado grande (máx. ${CSV_MAX_BYTES / 1024 / 1024} MB).`);
      return;
    }
    const text = await file.text();
    const rows = parseCsv(text);
    if (rows.length < 2) {
      toast.error('O ficheiro não contém linhas de dados.');
      return;
    }
    const headers = rows[0].map((h) => String(h).trim());
    const columnMap = autoMapColumns(headers);
    const mapped = mapRows(rows, columnMap);
    const validations = new Map<number, ReturnType<typeof validateRow>>();
    for (const r of mapped) validations.set(r.rowIndex, validateRow(r));
    const duplicates = findDuplicates(mapped);
    setCsv({ fileName: file.name, rows, headers, columnMap, mapped, validations, duplicates, dupStrategy: 'skip' });
  };

  const remapColumn = (colIdx: number, field: string | null) => {
    if (!csv) return;
    const columnMap = csv.columnMap.map((v, i) => (i === colIdx ? field : v));
    const mapped = mapRows(csv.rows, columnMap);
    const validations = new Map<number, ReturnType<typeof validateRow>>();
    for (const r of mapped) validations.set(r.rowIndex, validateRow(r));
    setCsv({ ...csv, columnMap, mapped, validations, duplicates: findDuplicates(mapped) });
  };

  const csvStats = useMemo(() => {
    if (!csv) return { total: 0, valid: 0, warnings: 0, errors: 0, dups: 0 };
    let valid = 0, warnings = 0, errors = 0;
    for (const [, v] of csv.validations) {
      if (v.status === 'valid') valid++; else if (v.status === 'warning') warnings++; else errors++;
    }
    return { total: csv.mapped.length, valid, warnings, errors, dups: csv.duplicates.size };
  }, [csv]);

  // --- Path C: native provisioning + catalog import ---
  const provisionNative = async () => {
    setBusy(true);
    try {
      const store = await onboardingApi.provisionStore({
        name: form.storeName, connectionType: 'native',
        currency: form.currency, slug: form.slug || undefined, onboardingCompleted: true,
      });
      const local = await activateStoreLocally(store, {});
      const cfg = { ...local.config, connectionType: 'native' as const, nativeStoreId: store.id };
      setNativeCfg(cfg as WooCommerceConfig);
      const result: ImportResult = { created: 0, updated: 0, skipped: 0, errors: [] };

      if (catalogMode === 'product' && firstProduct.name && firstProduct.regular_price) {
        try {
          await WooCommerceAPI.createProduct(cfg as WooCommerceConfig, {
            name: firstProduct.name, regular_price: firstProduct.regular_price, type: 'simple', status: 'publish',
            stock_quantity: firstProduct.stock_quantity ? parseInt(firstProduct.stock_quantity, 10) : null,
            manage_stock: !!firstProduct.stock_quantity, sku: firstProduct.sku || '',
          });
          result.created++;
        } catch (e: any) {
          result.errors.push({ rowIndex: 0, message: e?.message || 'Falha ao criar produto' });
        }
      }

      if (catalogMode === 'csv' && csv) {
        setImporting(true);
        try {
          // Create missing categories first so products resolve to IDs.
          const catNames = new Set<string>();
          for (const r of csv.mapped) {
            for (const name of (r.values.categories || '').split('|').map((s) => s.trim()).filter(Boolean)) catNames.add(name);
          }
          const catIds = new Map<string, number>();
          for (const name of catNames) {
            try {
              const created = await wooRequest(cfg as WooCommerceConfig, 'products/categories', { method: 'POST', body: { name } });
              if (created?.id) catIds.set(name.toLowerCase(), created.id);
            } catch { /* category may exist — best effort */ }
          }
          try {
            const existing = await wooRequest(cfg as WooCommerceConfig, 'products/categories?per_page=100');
            for (const c of existing || []) if (c?.name && c?.id) catIds.set(String(c.name).toLowerCase(), c.id);
          } catch { }

          for (const r of csv.mapped) {
            const v = csv.validations.get(r.rowIndex);
            if (v?.status === 'error') { result.skipped++; continue; }
            if (csv.dupStrategy === 'skip' && csv.duplicates.has(r.rowIndex)) { result.skipped++; continue; }
            try {
              await WooCommerceAPI.createProduct(cfg as WooCommerceConfig, toWooProduct(r, catIds));
              result.created++;
              setImportState({ ...result });
            } catch (e: any) {
              result.errors.push({ rowIndex: r.rowIndex, message: e?.message || 'Erro ao importar' });
            }
          }
        } finally {
          setImporting(false);
        }
      }

      setImportState(result);
      setProvisioned(store);
      go('c-payments');
    } catch (e: any) {
      toast.error(e?.message || 'Não foi possível criar a loja.');
    } finally {
      setBusy(false);
    }
  };

  // --- Payments step (native) — one provider, credentials saved server-side ---
  const payReady = () => {
    if (payUsePlatform && payProvider !== 'stripe') return true;
    if (payProvider === 'stripe') return !!payFields.stripe_secret_key?.trim();
    if (payProvider === 'mollie') return !!payFields.mollie_api_key?.trim();
    if (payProvider === 'viva') return !!(payFields.viva_client_id?.trim() && payFields.viva_client_secret?.trim());
    if (payProvider === 'adyen') return !!(payFields.adyen_api_key?.trim() && payFields.adyen_merchant_account?.trim());
    return false;
  };

  const savePayments = async () => {
    // On a resumed session nativeCfg is gone — activateStoreLocally already
    // wrote the store config to localStorage, so fall back to loadConfig().
    const cfg = nativeCfg || loadConfig();
    if (!cfg || cfg.connectionType !== 'native') { go('done'); return; }
    if (!payReady()) { toast.error('Preencha as credenciais do prestador escolhido.'); return; }
    setSavingPayments(true);
    try {
      await wooRequest(cfg, 'payments', {
        method: 'PUT',
        body: { provider: payProvider, use_platform: payUsePlatform, ...payFields },
      });
      toast.success('Pagamentos configurados.');
      go('done');
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao guardar o método de pagamento.');
    } finally {
      setSavingPayments(false);
    }
  };

  // --- logout of the wizard session ---
  const signOut = async () => {
    await onboardingSupabase.auth.signOut().catch(() => {});
    localStorage.removeItem('app_logged_in');
    window.location.href = '/login';
  };

  if (!sessionChecked) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-black"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  }

  const stepTitles: Record<StepId, string> = {
    'choose-path': 'Como quer começar?',
    'a-store': 'Ligar a sua loja WooCommerce',
    'a-credentials': 'Credenciais da API',
    'a-review': 'Rever e concluir',
    'b-install': 'Instalar o plugin LojaPilot',
    'b-pair': 'Emparelhar a loja',
    'c-details': 'A sua nova loja',
    'c-payments': 'Receber pagamentos',
    'c-catalog': 'Catálogo inicial',
    'done': 'Tudo pronto!',
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-black flex flex-col">
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-black/60 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <img src={isDark ? logoLight : logoDark} alt="LojaPilot" className="h-8 w-auto" />
          <div className="flex items-center gap-3">
            {path && (
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500">
                {PATH_STEPS[path].filter((s) => !['choose-path', 'done'].includes(s)).map((s, i, arr) => (
                  <React.Fragment key={s}>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${stepIndex > i + 1 || step === 'done' ? 'bg-emerald-500 text-white' : stepIndex === i + 1 ? 'bg-emerald-500/15 text-emerald-600 border border-emerald-500' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>{i + 1}</span>
                    {i < arr.length - 1 && <span className="w-4 h-px bg-slate-300 dark:bg-slate-700" />}
                  </React.Fragment>
                ))}
              </div>
            )}
            <button onClick={signOut} className="text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">Terminar sessão</button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-10">
        {resumed && step !== 'done' && (
          <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-300">
            Retomámos a sua configuração anterior — continue de onde parou.
          </div>
        )}

        <h1 className="text-2xl sm:text-3xl font-bold font-['Poppins'] text-slate-900 dark:text-white mb-2">{stepTitles[step]}</h1>

        {/* ============ CHOOSE PATH ============ */}
        {step === 'choose-path' && (
          <div className="grid gap-4 sm:grid-cols-3 mt-8">
            {([
              { id: 'woocommerce_api' as Path, icon: Globe, title: 'Ligar WooCommerce', desc: 'Já tenho uma loja WooCommerce — ligar via REST API (CK/CS).' },
              { id: 'woocommerce_plugin' as Path, icon: Plug, title: 'Plugin LojaPilot', desc: 'Ligar o WooCommerce com o plugin oficial — emparelhamento em 1 minuto.' },
              { id: 'native' as Path, icon: Store, title: 'Criar loja LojaPilot', desc: 'Começar do zero ou importar um catálogo CSV — tudo dentro do LojaPilot.' },
            ]).map((opt) => (
              <button key={opt.id} onClick={() => { setPath(opt.id); persist('choose-path', { path: opt.id }); go(PATH_STEPS[opt.id][1]); }}
                className="text-left p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111] hover:border-emerald-400 dark:hover:border-emerald-600 hover:shadow-lg transition-all group">
                <opt.icon className="w-8 h-8 text-emerald-500 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-slate-900 dark:text-white mb-1 font-['Poppins']">{opt.title}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">{opt.desc}</p>
              </button>
            ))}
          </div>
        )}

        {/* ============ A: WooCommerce API ============ */}
        {step === 'a-store' && (
          <Card className="max-w-xl mx-auto mt-6">
            <CardContent className="p-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="a-name">Nome da loja</Label>
                <Input id="a-name" value={form.storeName} onChange={(e) => setForm({ ...form, storeName: e.target.value })} placeholder="A Minha Loja" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="a-url">URL da loja WooCommerce</Label>
                <Input id="a-url" value={form.wpUrl} onChange={(e) => setForm({ ...form, wpUrl: e.target.value })} placeholder="https://aminhaloja.pt" />
                <p className="text-xs text-slate-500">O URL público do seu site WordPress — não inclua <code>/wp-json</code>.</p>
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={goBack}><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button>
                <Button onClick={() => { if (!form.storeName.trim() || !normalizedWpUrl()) { toast.error('Indique o nome e um URL válido.'); return; } go('a-credentials'); }}
                  style={{ backgroundColor: '#10B981', color: '#03291F' }}>Continuar<ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 'a-credentials' && (
          <Card className="max-w-xl mx-auto mt-6">
            <CardContent className="p-6 space-y-5">
              <p className="text-sm text-slate-500">No WordPress vá a <b>WooCommerce → Definições → Avançado → REST API</b> e crie uma chave com permissões <b>Ler/Escrever</b>.</p>
              <div className="space-y-2">
                <Label htmlFor="a-ck">Consumer Key</Label>
                <Input id="a-ck" value={form.consumerKey} onChange={(e) => { setForm({ ...form, consumerKey: e.target.value }); setConnTest({ state: 'idle' }); }} placeholder="ck_xxxxxxxx" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="a-cs">Consumer Secret</Label>
                <Input id="a-cs" type="password" value={form.consumerSecret} onChange={(e) => { setForm({ ...form, consumerSecret: e.target.value }); setConnTest({ state: 'idle' }); }} placeholder="cs_xxxxxxxx" />
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={testConnection} disabled={connTest.state === 'testing' || !form.consumerKey || !form.consumerSecret}>
                  {connTest.state === 'testing' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                  Testar ligação
                </Button>
                {connTest.state === 'ok' && <span className="text-sm text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Ligado</span>}
                {connTest.state === 'fail' && <span className="text-sm text-rose-600 flex items-center gap-1"><XCircle className="w-4 h-4" />{connTest.message}</span>}
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={goBack}><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button>
                <Button onClick={() => { if (!form.consumerKey.startsWith('ck_') || !form.consumerSecret.startsWith('cs_')) { toast.error('As chaves devem começar por ck_ e cs_.'); return; } go('a-review'); }} disabled={connTest.state !== 'ok'}
                  style={{ backgroundColor: '#10B981', color: '#03291F' }}>Continuar<ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 'a-review' && (
          <Card className="max-w-xl mx-auto mt-6">
            <CardContent className="p-6 space-y-4">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Loja</dt><dd className="font-medium">{form.storeName}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">URL</dt><dd className="font-medium">{normalizedWpUrl()}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Método</dt><dd className="font-medium">WooCommerce REST API</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Ligação</dt><dd className="text-emerald-600 font-medium flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Verificada</dd></div>
              </dl>
              <p className="text-xs text-slate-500">As credenciais são guardadas no servidor LojaPilot e nunca partilhadas. Pode desligar a loja a qualquer momento nas Definições.</p>
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={goBack}><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button>
                <Button onClick={provisionApi} disabled={busy} style={{ backgroundColor: '#10B981', color: '#03291F' }}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Criar ligação
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ============ B: Plugin ============ */}
        {step === 'b-install' && (
          <Card className="max-w-xl mx-auto mt-6">
            <CardContent className="p-6 space-y-5">
              <ol className="list-decimal list-inside space-y-3 text-sm text-slate-600 dark:text-slate-300">
                <li>Transfira o plugin <b>LojaPilot Connect</b>:<br />
                  <a href="/downloads/lojapilot-connect.zip" className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold hover:underline mt-1"><Download className="w-4 h-4" />lojapilot-connect.zip</a>
                </li>
                <li>No WordPress: <b>Plugins → Adicionar novo → Carregar plugin</b> e escolha o ficheiro.</li>
                <li>Ative o plugin — precisa de <b>WooCommerce ativo</b>.</li>
                <li>Abra <b>LojaPilot → Emparelhar</b> no menu do WordPress.</li>
              </ol>
              <div className="space-y-2 pt-2">
                <Label htmlFor="b-name">Nome da loja (opcional)</Label>
                <Input id="b-name" value={form.storeName} onChange={(e) => setForm({ ...form, storeName: e.target.value })} placeholder="A Minha Loja" />
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={goBack}><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button>
                <Button onClick={() => { go('b-pair'); if (!pairing) generateCode(); }} style={{ backgroundColor: '#10B981', color: '#03291F' }}>Já instalei — continuar<ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 'b-pair' && (
          <Card className="max-w-xl mx-auto mt-6">
            <CardContent className="p-6 space-y-5 text-center">
              {!pairing ? (
                <div className="py-8"><Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-500" /></div>
              ) : pairing.status === 'expired' ? (
                <div className="space-y-4 py-4">
                  <XCircle className="w-10 h-10 text-rose-500 mx-auto" />
                  <p className="text-sm text-slate-600">O código expirou (validade de 10 minutos).</p>
                  <Button onClick={generateCode} disabled={busy}>Gerar novo código</Button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-500">No plugin WordPress, cole este código de emparelhamento:</p>
                  <div className="flex items-center justify-center gap-3">
                    <code className="text-3xl font-mono font-bold tracking-[0.3em] text-emerald-500 bg-emerald-500/10 rounded-xl px-6 py-4">{pairing.code}</code>
                    <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(pairing.code); toast.success('Copiado!'); }}><Copy className="w-4 h-4" /></Button>
                  </div>
                  <p className="text-xs text-slate-500">Válido até {new Date(pairing.expiresAt).toLocaleTimeString('pt-PT')}. O código é de utilização única.</p>
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-500 py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />À espera que o plugin conclua o emparelhamento...
                  </div>
                </>
              )}
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={goBack}><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ============ C: Native store ============ */}
        {step === 'c-details' && (
          <Card className="max-w-xl mx-auto mt-6">
            <CardContent className="p-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="c-name">Nome da loja</Label>
                <Input id="c-name" value={form.storeName} onChange={(e) => setForm({ ...form, storeName: e.target.value, slug: form.slug || e.target.value })} placeholder="A Minha Loja" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="c-slug">Slug público</Label>
                  <Input id="c-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder="minha-loja" />
                </div>
                <div className="space-y-2">
                  <Label>Moeda</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR — Euro</SelectItem>
                      <SelectItem value="USD">USD — Dólar</SelectItem>
                      <SelectItem value="GBP">GBP — Libra</SelectItem>
                      <SelectItem value="BRL">BRL — Real</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-slate-500">A loja fica disponível em <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">/{form.slug || 'minha-loja'}</code>. De seguida importa o catálogo e configura como recebe pagamentos.</p>
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={goBack}><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button>
                <Button onClick={() => { if (!form.storeName.trim()) { toast.error('Indique o nome da loja.'); return; } go('c-catalog'); }}
                  style={{ backgroundColor: '#10B981', color: '#03291F' }}>Continuar<ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 'c-catalog' && (
          <Card className="max-w-3xl mx-auto mt-6">
            <CardContent className="p-6 space-y-5">
              <div className="flex flex-wrap gap-2">
                {([['skip', 'Começar vazia'], ['product', 'Adicionar produto'], ['csv', 'Importar CSV']] as const).map(([m, label]) => (
                  <button key={m} onClick={() => setCatalogMode(m)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${catalogMode === m ? 'bg-emerald-500 text-white border-emerald-500' : 'border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-emerald-400'}`}>{label}</button>
                ))}
              </div>

              {catalogMode === 'product' && (
                <div className="grid sm:grid-cols-2 gap-4 border border-slate-200 dark:border-slate-800 rounded-xl p-4">
                  <div className="space-y-2"><Label>Nome do produto *</Label><Input value={firstProduct.name} onChange={(e) => setFirstProduct({ ...firstProduct, name: e.target.value })} placeholder="Ex.: T-Shirt Básica" /></div>
                  <div className="space-y-2"><Label>SKU</Label><Input value={firstProduct.sku} onChange={(e) => setFirstProduct({ ...firstProduct, sku: e.target.value })} placeholder="TS-001" /></div>
                  <div className="space-y-2"><Label>Preço *</Label><Input value={firstProduct.regular_price} onChange={(e) => setFirstProduct({ ...firstProduct, regular_price: e.target.value })} placeholder="19.99" /></div>
                  <div className="space-y-2"><Label>Stock</Label><Input value={firstProduct.stock_quantity} onChange={(e) => setFirstProduct({ ...firstProduct, stock_quantity: e.target.value })} placeholder="50" /></div>
                </div>
              )}

              {catalogMode === 'csv' && (
                <div className="space-y-4">
                  {!csv ? (
                    <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-8 text-center">
                      <FileUp className="w-8 h-8 text-slate-400 mx-auto mb-3" />
                      <p className="text-sm text-slate-500 mb-4">CSV com colunas: nome, SKU, preço, stock, categorias, imagens…</p>
                      <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => e.target.files?.[0] && handleCsvFile(e.target.files[0])} />
                      <div className="flex items-center justify-center gap-3">
                        <Button variant="outline" onClick={() => fileRef.current?.click()}>Escolher ficheiro</Button>
                        <a href={`data:text/csv;charset=utf-8,${encodeURIComponent(CSV_TEMPLATE)}`} download="lojapilot-template.csv" className="text-xs text-emerald-600 hover:underline">Descarregar modelo CSV</a>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">{csv.fileName} — {csvStats.total} linhas</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline" className="text-emerald-600 border-emerald-300">{csvStats.valid} válidas</Badge>
                            {csvStats.warnings > 0 && <Badge variant="outline" className="text-amber-600 border-amber-300">{csvStats.warnings} avisos</Badge>}
                            {csvStats.errors > 0 && <Badge variant="outline" className="text-rose-600 border-rose-300">{csvStats.errors} erros</Badge>}
                            {csvStats.dups > 0 && <Badge variant="outline" className="text-amber-600 border-amber-300">{csvStats.dups} duplicados</Badge>}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setCsv(null)}>Trocar ficheiro</Button>
                      </div>

                      <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-lg">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 dark:bg-slate-900">
                            <tr>
                              <th className="px-2 py-2 text-left">Coluna CSV</th>
                              {csv.headers.map((h, i) => (
                                <th key={i} className="px-2 py-2 text-left font-medium">
                                  <div className="text-slate-500 mb-1">{h}</div>
                                  <Select value={csv.columnMap[i] || 'none'} onValueChange={(v) => remapColumn(i, v === 'none' ? null : v)}>
                                    <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">— Ignorar —</SelectItem>
                                      {PRODUCT_FIELDS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}{f.required ? ' *' : ''}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </th>
                              ))}
                              <th className="px-2 py-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {csv.mapped.slice(0, 10).map((r) => {
                              const v = csv.validations.get(r.rowIndex);
                              const dup = csv.duplicates.get(r.rowIndex);
                              return (
                                <tr key={r.rowIndex} className="border-t border-slate-100 dark:border-slate-800">
                                  <td className="px-2 py-1.5">
                                    {v?.status === 'error' ? <XCircle className="w-4 h-4 text-rose-500" /> : v?.status === 'warning' || dup ? <span className="w-4 h-4 inline-block text-amber-500">!</span> : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                                  </td>
                                  {csv.headers.map((_, ci) => <td key={ci} className="px-2 py-1.5 text-slate-600 dark:text-slate-300 max-w-[140px] truncate">{r.raw[ci]}</td>)}
                                  <td className="px-2 py-1.5 text-[10px] text-slate-400 max-w-[160px] truncate">{[...(v?.errors || []), ...(v?.warnings || []), dup].filter(Boolean).join('; ')}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {csv.mapped.length > 10 && <p className="text-xs text-slate-400 px-3 py-2">… e mais {csv.mapped.length - 10} linhas</p>}
                      </div>

                      {csvStats.dups > 0 && (
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-slate-500">Linhas duplicadas:</span>
                          <Select value={csv.dupStrategy} onValueChange={(v) => setCsv({ ...csv, dupStrategy: v as 'skip' | 'import' })}>
                            <SelectTrigger className="h-8 w-48"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="skip">Ignorar duplicados</SelectItem>
                              <SelectItem value="import">Importar na mesma</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={goBack}><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button>
                <Button onClick={provisionNative} disabled={busy || importing} style={{ backgroundColor: '#10B981', color: '#03291F' }}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Package className="w-4 h-4 mr-2" />}Criar loja
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ============ PATH C: PAYMENTS ============ */}
        {step === 'c-payments' && (
          <Card className="max-w-xl mx-auto mt-6">
            <CardContent className="p-6 space-y-5">
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">Como quer receber pagamentos online?</h3>
                <p className="text-xs text-slate-500 mt-1">Escolha um prestador e cole as credenciais — ficam guardadas em segurança no servidor e o checkout da sua loja passa a aceitar pagamentos reais. Pode configurar depois em Subscrição → Pagamentos da Loja.</p>
              </div>

              <div className="space-y-2">
                <Label>Prestador de pagamento</Label>
                <Select value={payProvider} onValueChange={setPayProvider}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stripe">Stripe</SelectItem>
                    <SelectItem value="mollie">Mollie</SelectItem>
                    <SelectItem value="viva">Viva Wallet</SelectItem>
                    <SelectItem value="adyen">Adyen</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {payProvider === 'stripe' && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Ligue a sua conta Stripe — a Stripe valida os seus dados (KYC) e o dinheiro das vendas cai diretamente no seu IBAN, sem passar pela plataforma.
                  </p>
                  <Button onClick={() => setConnectOpen(true)} style={{ backgroundColor: '#10B981', color: '#03291F' }}>
                    Ligar Stripe<ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <p className="text-xs text-slate-500">Stripe Connect — onboarding seguro dentro da app (empresa, titular, IBAN).</p>
                </div>
              )}

              {payProvider !== 'stripe' && (
                <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 dark:border-slate-800 p-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={payUsePlatform}
                    onChange={e => setPayUsePlatform(e.target.checked)}
                    className="w-4 h-4 mt-0.5 rounded accent-emerald-600"
                  />
                  <span>
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Modo teste da plataforma</span>
                    <span className="block text-xs text-slate-500 mt-0.5">
                      Usa as credenciais sandbox da LojaPilot — sem pagamentos reais. Para receber na sua conta, insira as suas credenciais.
                    </span>
                  </span>
                </label>
              )}

              {payProvider === 'mollie' && !payUsePlatform && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>API Key *</Label>
                    <Input type="password" placeholder="live_... ou test_..." value={payFields.mollie_api_key || ''} onChange={(e) => setPayFields(f => ({ ...f, mollie_api_key: e.target.value }))} />
                  </div>
                  <p className="text-xs text-slate-500">Mollie Dashboard → Developers → API keys. Suporta cartão, MB WAY e Multibanco conforme a sua conta Mollie.</p>
                </div>
              )}

              {payProvider === 'viva' && !payUsePlatform && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Client ID *</Label>
                    <Input placeholder="Client ID da app Viva" value={payFields.viva_client_id || ''} onChange={(e) => setPayFields(f => ({ ...f, viva_client_id: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Secret *</Label>
                    <Input type="password" placeholder="Client Secret" value={payFields.viva_client_secret || ''} onChange={(e) => setPayFields(f => ({ ...f, viva_client_secret: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Ambiente</Label>
                    <Select value={payFields.viva_environment || 'live'} onValueChange={(v) => setPayFields(f => ({ ...f, viva_environment: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="live">Produção</SelectItem>
                        <SelectItem value="demo">Demo / Teste</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-slate-500">Viva Wallet → Developers → Smart Checkout credentials.</p>
                </div>
              )}

              {payProvider === 'adyen' && !payUsePlatform && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>API Key *</Label>
                    <Input type="password" placeholder="AQE... (Checkout API key)" value={payFields.adyen_api_key || ''} onChange={(e) => setPayFields(f => ({ ...f, adyen_api_key: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Merchant Account *</Label>
                    <Input placeholder="Nome da conta merchant Adyen" value={payFields.adyen_merchant_account || ''} onChange={(e) => setPayFields(f => ({ ...f, adyen_merchant_account: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Ambiente</Label>
                    <Select value={payFields.adyen_environment || 'test'} onValueChange={(v) => setPayFields(f => ({ ...f, adyen_environment: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="test">Teste</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-slate-500">Adyen Customer Area → Developers → API credentials. Checkout hosted com os métodos da sua conta.</p>
                </div>
              )}

              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={() => go('done')}>Configurar depois</Button>
                {payProvider !== 'stripe' && (
                  <Button onClick={savePayments} disabled={savingPayments || !payReady()} style={{ backgroundColor: '#10B981', color: '#03291F' }}>
                    {savingPayments ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Guardar e concluir<ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ============ DONE ============ */}
        {step === 'done' && (
          <Card className="max-w-xl mx-auto mt-6">
            <CardContent className="p-8 text-center space-y-5">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
              <div>
                <h2 className="text-xl font-bold font-['Poppins'] text-slate-900 dark:text-white">{provisioned?.name || form.storeName} está pronta</h2>
                <p className="text-sm text-slate-500 mt-1">A sua loja foi configurada e já está ligada ao LojaPilot.</p>
              </div>
              {importState && (importState.created > 0 || importState.errors.length > 0 || importState.skipped > 0) && (
                <div className="text-sm bg-slate-50 dark:bg-slate-900 rounded-lg p-4 space-y-1">
                  {importState.created > 0 && <p className="text-emerald-600 font-medium">{importState.created} produto(s) importados</p>}
                  {importState.skipped > 0 && <p className="text-amber-600">{importState.skipped} linha(s) ignoradas</p>}
                  {importState.errors.slice(0, 5).map((e, i) => <p key={i} className="text-rose-600 text-xs">Linha {e.rowIndex + 2}: {e.message}</p>)}
                  {importState.errors.length > 5 && <p className="text-rose-600 text-xs">+{importState.errors.length - 5} erros</p>}
                </div>
              )}
              <Button className="w-full h-11" style={{ backgroundColor: '#10B981', color: '#03291F' }} onClick={() => { onboardingApi.saveProgress('done', {}).catch(() => {}); window.location.href = '/login'; }}>
                Abrir o dashboard<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
      {/* Stripe Connect embedded onboarding — resolves store config the same
          way savePayments does (nativeCfg is lost on resumed sessions). */}
      {(() => {
        const cfg = nativeCfg || loadConfig();
        return cfg?.connectionType === 'native' ? (
          <StripeConnectDialog
            open={connectOpen}
            onOpenChange={setConnectOpen}
            config={cfg}
            onExit={() => go('done')}
          />
        ) : null;
      })()}
      <Toaster richColors position="top-right" theme={isDark ? 'dark' : 'light'} />
    </div>
  );
}
